import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import axiosInstance from 'Services/APIService';
import { CarePlan, CarePlanState } from '../../../types/ndisSection/carePlanInfoTypes';

// Define the support item type based on API response
interface SupportItem {
  SupportItemNumber: string;
  SupportItemName: string;
  RegistrationGroupName: string;
  SupportCategoryName: string;
  SupportCategoryNamePACE: string;
  State: string;
  Value: number;
}

// Extend the state to include support items
interface ExtendedCarePlanState extends CarePlanState {
  supportItems: SupportItem[];
  loadingSupportItems: boolean;
}

const initialState: ExtendedCarePlanState = {
  carePlan: null,
  selectedSection: 'Personal Info',
  loading: false,
  error: null,
  supportItems: [],
  loadingSupportItems: false
};

export const fetchCarePlan = createAsyncThunk(
  'carePlanInfo/fetchCarePlan',
  async (
    payload: {
      event_id?: string;
      user_whoRequested_id?: string;
      group_id?: string;
      service_name_id?: string;
      nested_component_id?: string;
      client_name?: string;
    },
    { rejectWithValue }
  ) => {
    try {
      const response = await axiosInstance.post('/client/get-care-plan', payload);
      return response.data as CarePlan;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to fetch care plan');
    }
  }
);

export const fetchSupportItems = createAsyncThunk(
  'carePlanInfo/fetchSupportItems',
  async (
    payload: {
      group_id: string;
      service_name_id: string;
      query?: string;
    },
    { rejectWithValue }
  ) => {
    try {
      const response = await axiosInstance.get('/ndis/corporate-services/get-support-items', {
        params: {
          group_id: payload.group_id,
          service_name_id: payload.service_name_id,
          query: payload.query
        }
      });
      return response.data as SupportItem[];
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to fetch support items');
    }
  }
);

export const setCarePlanAsync = createAsyncThunk(
  'carePlanInfo/setCarePlan',
  async (
    payload: {
      carePlan: CarePlan;
      user_whoRequested_id?: string;
      group_id?: string;
      service_name_id?: string;
      nested_component_id?: string;
      client_name?: string;
    },
    { rejectWithValue }
  ) => {
    try {
      const formattedPayload = {
        payload_ids: {
          client_name: payload.client_name || '',
          group_id: payload.group_id || '',
          service_name_id: payload.service_name_id || '',
          nested_component_id: payload.nested_component_id || ''
        },
        data_updated: payload.carePlan
      };
      const response = await axiosInstance.post('/client/update-care-plan', formattedPayload);
      return response.data as CarePlan;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to save care plan');
    }
  }
);

const carePlanInfoSlice = createSlice({
  name: 'carePlanInfo',
  initialState,
  reducers: {
    setCarePlan(state, action: PayloadAction<CarePlan>) {
      state.carePlan = action.payload;
    },
    setSelectedSection(state, action: PayloadAction<string>) {
      state.selectedSection = action.payload;
    },
    resetError(state) {
      state.error = null;
    },
    clearSupportItems(state) {
      state.supportItems = [];
      state.loadingSupportItems = false;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchCarePlan.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchCarePlan.fulfilled, (state, action) => {
        state.loading = false;
        state.carePlan = action.payload;
      })
      .addCase(fetchCarePlan.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(setCarePlanAsync.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(setCarePlanAsync.fulfilled, (state, action) => {
        state.loading = false;
        state.carePlan = action.payload;
      })
      .addCase(setCarePlanAsync.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(fetchSupportItems.pending, (state) => {
        state.loadingSupportItems = true;
        state.error = null;
      })
      .addCase(fetchSupportItems.fulfilled, (state, action) => {
        state.loadingSupportItems = false;
        state.supportItems = action.payload;
      })
      .addCase(fetchSupportItems.rejected, (state, action) => {
        state.loadingSupportItems = false;
        state.error = action.payload as string;
      });
  }
});

export const { setCarePlan, setSelectedSection, resetError, clearSupportItems } =
  carePlanInfoSlice.actions;
export default carePlanInfoSlice.reducer;
